
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit, X, Save, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EditableCardProps {
  title: string;
  description?: string;
  isEditing: boolean;
  onToggleEdit: () => void;
  onSave?: () => void;
  children: React.ReactNode;
  className?: string;
  showEditButton?: boolean;
  icon?: LucideIcon;
  iconColor?: string;
}

const EditableCard: React.FC<EditableCardProps> = ({
  title,
  description,
  isEditing,
  onToggleEdit,
  onSave,
  children,
  className,
  showEditButton = true,
  icon: Icon,
  iconColor = "text-gray-600"
}) => {
  return (
    <Card className={cn("admin-card admin-card-stats", className)}>
      <CardHeader className="pb-4">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            {Icon && <Icon className={cn("h-5 w-5", iconColor)} />}
            {title}
          </CardTitle>
          {description && (
            <CardDescription className="text-sm text-muted-foreground">
              {description}
            </CardDescription>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isEditing ? (
          <div className="space-y-4">
            {children}
            {showEditButton && onSave && (
              <div className="flex gap-2 pt-2 border-t">
                <Button 
                  size="sm" 
                  onClick={onSave}
                  className="flex-1"
                >
                  <Save className="h-3 w-3 mr-1" />
                  Save
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onToggleEdit}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            {children}
            {showEditButton && (
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full mt-3" 
                onClick={onToggleEdit}
              >
                <Edit className="h-3 w-3 mr-2" />
                Edit
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EditableCard;
